import * as React from 'react';
import { DataGrid, SelectOption } from '@SBGSports/referee-react';
import { useTranslations } from 'i18n';
import { DeepActivityPeriod } from 'oas';
import { useMemo, useRef, useEffect } from 'react';
import { ColDef } from 'ag-grid-community';
import { CellDropdownSelectRenderer } from './cell-dropdown-select-renderer';
import { DATE_FNS_ACTIVITY_EDITOR_TIME_FORMAT, getFormattedDuration, useDateFormatter } from 'shared';
import { CellDropdownSelectProps } from './cell-dropdown-select';
import { calcTableHeight } from '../../../reporting/helpers';
import { cellStyle, getRowStyle } from '../../focus-utils';
import { useFocusStoredOptions } from 'focus/use-focus-stored-options';
import { TIMING_OPTION } from 'focus/constants';
import { DEFAULT_TABLE_COLUMN_DEF } from '../../../reporting/constants';

export interface FocusTableProps {
    periods: DeepActivityPeriod[];
    syncToFocusOptions: SelectOption[];
    selectOptionsMap: Map<string, string>;
    setSelectOptionsMap: (map: Map<string, string>) => void;
}

export const SyncToFocusTable: React.FC<FocusTableProps> = (props) => {
    const { periods, syncToFocusOptions, selectOptionsMap, setSelectOptionsMap } = props;

    //Hooks
    const { __ } = useTranslations();
    const formatDate = useDateFormatter();
    const { focusStoredOptions } = useFocusStoredOptions();

    ////Sync between persistence and available options, then set period alignment selections in selectionsRef so its available in the provider after first render
    const initialDataSet = useRef(false);
    useEffect(() => {
        if (initialDataSet.current) {
            return;
        }

        const initialSelection = new Map<string, string>();
        periods.forEach((period) => {
            const periodId = period.id ?? '';
            if (selectOptionsMap.has(periodId)) {
                initialSelection.set(periodId, selectOptionsMap.get(periodId) ?? '');
            }
        });

        setSelectOptionsMap(initialSelection);
        initialDataSet.current = true;
    }, [periods, selectOptionsMap, setSelectOptionsMap]);

    const FOCUS_COLUMN_DEF: ColDef[] = [
        {
            flex: 2,
            headerName: __('smt_importer.openfield_periods'),
            field: 'openfield_periods',
            sortable: true,
            cellStyle: cellStyle,
        },
        {
            flex: 1,
            headerName: __('misc.start_time'),
            field: 'start_time',
            sortable: true,
            cellStyle: cellStyle,
        },
        {
            flex: 1,
            headerName: __('misc.duration'),
            field: 'duration',
            sortable: true,
            cellStyle: cellStyle,
        },
        {
            flex: 2,
            headerName:
                focusStoredOptions.alignPeriodOption === TIMING_OPTION
                    ? __('smt_importer.timing_tag')
                    : __('smt_importer.focus_tag_start_time'),
            field: 'sync_to_focus',
            cellRenderer: CellDropdownSelectRenderer,
            cellStyle: cellStyle,
        },
    ];

    const rowData = useMemo(() => {
        return periods.map((period) => {
            // set selection for period
            const periodOptions = selectOptionsMap.has(period.id ?? '')
                ? syncToFocusOptions.map((opt) => {
                      if (opt.id === selectOptionsMap.get(period.id ?? '')) {
                          return { ...opt, isSelected: true };
                      }
                      return opt;
                  })
                : syncToFocusOptions;

            return {
                openfield_periods: period.name,
                start_time: formatDate(
                    new Date((period?.start_time ?? 0) * 1000),
                    DATE_FNS_ACTIVITY_EDITOR_TIME_FORMAT,
                ),
                duration: getFormattedDuration(period.start_time ?? 0, period.end_time ?? 0),
                select: {
                    testId: period.id,
                    options: periodOptions,
                    onChange: (value) => {
                        const mapUpdate = selectOptionsMap;
                        if (value) {
                            if (period.id) {
                                mapUpdate.set(period.id, String(value.id));
                            }
                            for (const [identity, optionId] of mapUpdate.entries()) {
                                if (period.id !== identity && optionId === value.id) {
                                    mapUpdate.delete(identity);
                                }
                            }
                        } else {
                            mapUpdate.delete(period.id ?? '');
                        }

                        setSelectOptionsMap(new Map([...mapUpdate]));
                    },
                } as CellDropdownSelectProps,
            };
        });
    }, [formatDate, periods, selectOptionsMap, setSelectOptionsMap, syncToFocusOptions]);

    return (
        <div style={{ height: `${calcTableHeight(rowData)}` }}>
            <DataGrid
                testId="focus-table"
                defaultColDef={DEFAULT_TABLE_COLUMN_DEF}
                columnDefs={FOCUS_COLUMN_DEF}
                rowData={rowData}
                getRowStyle={getRowStyle}
                gridOptions={{
                    overlayNoRowsTemplate: `${__('misc.no_data_available')}`,
                }}
                dense
            />
        </div>
    );
};
